import React from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatCurrency, formatNumber } from '../utils';

import { HomepageMetricsDisplayRow, HomepageMetricsSortDirection, HomepageMetricsSortField } from './types';

const COLUMNS: Array<{ field: HomepageMetricsSortField; label: string }> = [
  { field: 'cpOrder', label: '#' },
  { field: 'displayName', label: 'Section' },
  { field: 'views', label: 'Views' },
  { field: 'clicks', label: 'Clicks' },
  { field: 'orders', label: 'Orders' },
  { field: 'grossSales', label: 'Gross Sales (AUD)' },
  { field: 'margin', label: 'Margin (AUD)' },
];

type Props = {
  rows: Array<HomepageMetricsDisplayRow>;
  sortField: HomepageMetricsSortField;
  sortDirection: HomepageMetricsSortDirection;
  onSort: (field: HomepageMetricsSortField) => void;
  getLink?: (row: HomepageMetricsDisplayRow) => string;
  viewsDescription?: string;
};

export default function HomepageMetricsTable({
  rows,
  sortField,
  sortDirection,
  onSort,
  getLink,
  viewsDescription,
}: Props) {
  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            {COLUMNS.map(({ field, label }) => (
              <TableCell key={field} align={field === 'displayName' ? 'left' : 'right'}>
                <TableSortLabel
                  active={sortField === field}
                  direction={sortField === field ? sortDirection : 'desc'}
                  onClick={() => onSort(field)}
                >
                  <Tooltip title={field === 'views' ? (viewsDescription ?? '') : ''} describeChild>
                    <Typography fontWeight="bold" variant="body2">
                      {getLink && field === 'displayName' ? 'Carousel' : label}
                    </Typography>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">Days w/ Views</TableCell>
            {getLink && <TableCell align="center">Link</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.sectionId} hover>
              <TableCell align="right">{row.cpOrder + 1}</TableCell>
              <TableCell>
                <Tooltip title={row.sectionId}>
                  <Typography variant="body2" fontWeight="medium">
                    {row.displayName}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="right">{formatNumber(row.views)}</TableCell>
              <TableCell align="right">{formatNumber(row.clicks)}</TableCell>
              <TableCell align="right">{row.orders === null ? 'Not applicable' : formatNumber(row.orders)}</TableCell>
              <TableCell align="right">
                {row.grossSalesAud === null ? 'Not applicable' : formatCurrency(row.grossSalesAud)}
              </TableCell>
              <TableCell align="right">
                {row.marginAud === null ? 'Not applicable' : formatCurrency(row.marginAud)}
              </TableCell>
              <TableCell align="right">{row.daysWithViews}</TableCell>
              {getLink && (
                <TableCell align="center">
                  <IconButton
                    size="small"
                    href={getLink(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${row.displayName} on CP`}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
